import { MAX_POLLS } from '../../hooks/useLeakTest'
import Button from '../../ui/Button'
import { leakTest } from '../../content/site'
import styles from './PollTracker.module.css'

interface PollTrackerProps {
    poll: number
    running: boolean
    elapsedMs: number
    resolverCount: number
    /** Poll index at which each resolver first appeared. */
    firstSeen: Map<string, number>
    onStop: () => void
}

/**
 * Ten discrete cells rather than a progress ring.
 *
 * A smooth arc implies continuous progress toward a known end; the reality is
 * ten attempts with widening gaps (1s to 6s) where useful data can arrive at any
 * of them. Uneven cells filling is both more honest and more reassuring, because
 * the long pauses read as expected rather than as a hang.
 */
export default function PollTracker({
    poll, running, elapsedMs, resolverCount, firstSeen, onStop,
}: PollTrackerProps) {
    const log = [...firstSeen.entries()]
        .sort((a, b) => a[1] - b[1])
        .slice(-5)

    return (
        <div className={styles.tracker}>
            <div className={styles.cells} role="img"
                aria-label={`Poll ${Math.min(poll + 1, MAX_POLLS)} of ${MAX_POLLS}`}>
                {Array.from({ length: MAX_POLLS }, (_, i) => (
                    <span key={i} className={styles.cell}>
                        <span
                            className={i < poll ? styles.done : i === poll && running ? styles.active : styles.pending}
                        />
                        <span className={styles.n} aria-hidden="true">{i + 1}</span>
                    </span>
                ))}
            </div>

            {/* the widening gaps are the design, so say so rather than letting a
                six-second pause read as a hang -- but only while it is happening */}
            {running && (
                <p className={styles.honest}>
                    Polls are spaced unevenly on purpose — a long gap is expected, not a hang.
                </p>
            )}

            <p className={styles.status} role="status" aria-live="polite">
                {running
                    ? `${(elapsedMs / 1000).toFixed(0)}s elapsed · ${resolverCount} resolver${resolverCount === 1 ? '' : 's'} found`
                    : `Test finished — ${resolverCount} resolver${resolverCount === 1 ? '' : 's'} found in ${(elapsedMs / 1000).toFixed(0)}s.`}
            </p>

            {/* The streaming discovery log turns dead waiting into visible
                progress, but once the test is done it is stale scrollback taking
                up space -- the resolvers it named are in the results below. So it
                collapses to the single line above the moment the test finishes. */}
            {running && log.length > 0 && (
                <ul className={styles.log}>
                    {log.map(([ip, at]) => (
                        <li key={ip}>
                            <span className={styles.dim}>poll {at + 1}</span> {ip}
                        </li>
                    ))}
                </ul>
            )}

            {running && (
                <div className={styles.actions}>
                    <Button onClick={onStop} className={styles.stop}>{leakTest.stop}</Button>
                    <span className={styles.note}>Results so far are already valid.</span>
                </div>
            )}
        </div>
    )
}
